import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import axios from 'axios';
import { Space, Tooltip, Button, Dropdown, Menu, Modal, Form, Input, message } from 'antd';
import {
  DownloadOutlined,
  MailOutlined,
  EyeOutlined,
  EditOutlined,
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useUser } from '../../utils/userContext';

function VendorInvoicesDashboardPage() {
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [updatingInvoiceId, setUpdatingInvoiceId] = useState(null);
  const [emailForm] = Form.useForm();
  const [sendingEmail, setSendingEmail] = useState(false);
  const token = localStorage.getItem("token");
  const { user } = useUser();

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        if (!user || !user.OfficialEmail) {
          message.error("User not logged in or email not found. Please log in again.");
          setLoading(false);
          return;
        }
        const URL = process.env.REACT_APP_BE_URL + '/getConsultantInvoices';
        const response = await axios.get(URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.status === 200) {
          console.log("API Response:", response.data.invoices);
          // Filter invoices by agencyEmail matching user.OfficialEmail
          const filteredInvoices = response.data.invoices
            .filter(inv => inv.agencyEmail === user.OfficialEmail)
            .map(inv => ({
              ...inv,
              invoiceId: inv.invoiceNo,
              consultant: inv.consultantEmail,
              consultantName: inv.agency,
              month: moment(inv.startDate).format("MMMM"),
              year: moment(inv.startDate).year(),
              amount: inv.amount || 0,
              status: inv.status || "pending",
              generatedDate: inv.startDate,
              agency: inv.agency,
              agencyEmail: inv.agencyEmail,
              invoiceFile: inv.invoiceFile,
              startDate: inv.startDate,
              endDate: inv.endDate,
              generatingAgencyName: inv.generatingAgencyName,
              consultantAgency: inv.consultantAgency,
            }));
          setInvoices(filteredInvoices);
          if (filteredInvoices.length === 0) {
            message.warning("No invoices found for your account.");
          }
        } else {
          message.error("Failed to fetch invoices: Invalid response status.");
        }
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
        message.error(`Failed to fetch invoices: ${error.message || "Unknown error"}`);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [user, token]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv =>
      inv.invoiceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.consultant.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.consultantName && inv.consultantName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      inv.month.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(inv.year).includes(searchQuery) ||
      String(inv.amount).includes(searchQuery) ||
      inv.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.agency && inv.agency.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (inv.agencyEmail && inv.agencyEmail.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery, invoices]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredInvoices.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredInvoices.length / recordsPerPage);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  // Status dropdown menu
  const getStatusDropdownMenu = (invoice) => (
    <Menu
      onClick={({ key }) => handleStatusUpdate(invoice, key)}
      style={{
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        border: "1px solid #f0f0f0",
      }}
    >
      <Menu.Item
        key="Paid"
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <CheckOutlined style={{ color: "#52c41a", marginRight: "8px" }} />
        Paid
      </Menu.Item>
      <Menu.Item
        key="Rejected"
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        <CloseOutlined style={{ color: "#ff4d4f", marginRight: "8px" }} />
        Rejected
      </Menu.Item>
    </Menu>
  );

  const handleStatusUpdate = async (invoice, newStatus) => {
    setUpdatingPayment(true);
    setUpdatingInvoiceId(invoice.id);
    try {
      console.log(`Updating status for invoice ${invoice.invoiceId} to ${newStatus}`);
      setInvoices(prevInvoices =>
        prevInvoices.map(inv =>
          inv.invoiceId === invoice.invoiceId
            ? { ...inv, status: newStatus }
            : inv
        )
      );
      message.success(`Status updated to ${newStatus} successfully!`);
    } catch (error) {
      console.error("Status update error:", error);
      message.error(`Failed to update status: ${error.message || "Unknown error"}`);
    } finally {
      setUpdatingPayment(false);
      setUpdatingInvoiceId(null);
    }
  };

  const handleGenerateInvoice = async (invoice) => {
    if (!invoice || !invoice.invoiceFile) {
      message.error(`No invoice file available for ${invoice?.invoiceId || "this invoice"}`);
      return;
    }

    setGeneratingInvoice(true);
    try {
      // Create a new window/tab and load the PDF
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Invoice ${invoice.invoiceId}</title>
              <style>
                body, html {
                  margin: 0;
                  padding: 0;
                  height: 100%;
                  overflow: hidden;
                }
                iframe {
                  border: none;
                  width: 100%;
                  height: 100%;
                  position: fixed;
                  top: 0;
                  left: 0;
                }
              </style>
            </head>
            <body>
              <iframe src="${invoice.invoiceFile}"></iframe>
            </body>
          </html>
        `);
        message.success(`Invoice ${invoice.invoiceId} opened in new tab`);
      } else {
        message.error("Pop-up blocked. Please allow pop-ups for this site.");
      }
    } catch (error) {
      console.error("Invoice view error:", error);
      message.error(`Failed to open invoice ${invoice.invoiceId}: ${error.message || "Unknown error"}`);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleEmailClick = (invoice) => {
    setSelectedInvoice(invoice);
    setIsEmailModalVisible(true);
    emailForm.resetFields();
  };

  const handleEmailModalCancel = () => {
    setIsEmailModalVisible(false);
    emailForm.resetFields();
  };

  const handleEmailSend = async (values) => {
    setSendingEmail(true);
    try {
      if (!selectedInvoice) {
        message.error("No invoice selected.");
        return;
      }

      // Prepare email subject and body
      const subject = `Invoice ${selectedInvoice.invoiceId} from BYT`;
      const body = `Please find the details for Invoice No: ${selectedInvoice.invoiceId}\nVendor: ${selectedInvoice.agency}\nVendor Email: ${selectedInvoice.agencyEmail}\nConsultant Email: ${selectedInvoice.consultant}\nMonth: ${selectedInvoice.month}\nYear: ${selectedInvoice.year}\nStatus: ${selectedInvoice.status}\n\nPlease download the invoice file separately and attach it to this email.`;

      // Open the default email client with pre-filled details
      window.location.href = `mailto:${values.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      message.success(`Email client opened for ${values.email}. Please download the invoice file separately and attach it manually.`);

      setIsEmailModalVisible(false);
      emailForm.resetFields();
    } catch (error) {
      console.error("Email preparation error:", error);
      message.error(`Failed to prepare email: ${error.message || "Unknown error"}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleViewClick = (invoice) => {
    setSelectedInvoice(invoice);
    setIsViewModalVisible(true);
  };

  const handleViewModalCancel = () => {
    setIsViewModalVisible(false);
    setSelectedInvoice(null);
  };

  // Actions column render function
  const renderActions = (invoice) => {
    const isPaid = invoice.status === "Paid";
    const isRejected = invoice.status === "Rejected";
    const isUpdating = updatingPayment && updatingInvoiceId === invoice.id;

    return (
      <Space size={6}>
        {/* Download Button */}
        <Tooltip title="View Invoice">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="small"
            loading={generatingInvoice && selectedInvoice?.invoiceId === invoice.invoiceId}
            style={{
              backgroundColor: "#004f7c",
              borderColor: "#004f7c",
              fontSize: "12px",
              padding: "4px 8px",
              height: "28px",
              minWidth: "28px"
            }}
            onClick={() => handleGenerateInvoice(invoice)}
          />
        </Tooltip>

        {/* Email Button */}
        <Tooltip title="Send Email">
          <Button
            type="default"
            icon={<MailOutlined />}
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEmailClick(invoice);
              return false;
            }}
            style={{
              borderColor: "#004f7c",
              color: "#004f7c",
              fontSize: "12px",
              padding: "4px 8px",
              height: "28px",
              minWidth: "28px"
            }}
          />
        </Tooltip>

        {/* View Button */}
        <Tooltip
          title={
            isPaid
              ? "View Paid Invoice"
              : isRejected
                ? "View Rejected Invoice"
                : "View Invoice"
          }
        >
          <Button
            type="default"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewClick(invoice)}
            style={{
              borderColor: isPaid
                ? "#52c41a"
                : isRejected
                  ? "#ff4d4f"
                  : "#004f7c",
              color: isPaid
                ? "#52c41a"
                : isRejected
                  ? "#ff4d4f"
                  : "#004f7c",
              backgroundColor: isPaid
                ? "#f6ffed"
                : isRejected
                  ? "#fff2f0"
                  : "transparent",
              fontSize: "12px",
              padding: "4px 8px",
              height: "28px",
              minWidth: "28px"
            }}
          />
        </Tooltip>

        {/* Status Update Dropdown (commented out) */}
        {/* {!isPaid && invoice.status !== "Rejected" && (
          <Tooltip title="Update Payment Status">
            <Dropdown
              overlay={getStatusDropdownMenu(invoice)}
              trigger={["click"]}
              placement="bottomRight"
              disabled={isUpdating}
            >
              <Button
                type="default"
                loading={isUpdating}
                size="small"
                style={{
                  borderColor: "#004f7c",
                  color: "#004f7c",
                  fontSize: "11px",
                  padding: "4px 6px",
                  height: "28px",
                  minWidth: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {!isUpdating && (
                  <>
                    <EditOutlined style={{ fontSize: "11px", marginRight: "2px" }} />
                    <CaretDownOutlined style={{ fontSize: "10px" }} />
                  </>
                )}
              </Button>
            </Dropdown>
          </Tooltip>
        )} */}
      </Space>
    );
  };

  return (
    <div className="content-page">
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col">
            <h3 className="title mb-2">Vendor Invoice Dashboard</h3>
          </div>
        </div>
        <div className="row align-items-center justify-content-center">
          <div className="col-5">
            <div className="input-group mb-0">
              <input
                type="search"
                className="form-control search-input"
                placeholder="Keyword search..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <div className="input-group-append">
                <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                  <i className="ri-menu-search-line"></i>
                </button>
              </div>
            </div>
            <small className="form-text text-muted text-center mt-0">
              <i className="ri-information-2-line"></i> Search by Invoice, Vendor, Vendor Email, Status
            </small>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-12">
            <div className="table-responsive asset-information">
              <table className="table grid">
                <thead className="bg-light">
                  <tr>
                    <th>Invoice No</th>
                    <th>Vendor</th>
                    <th>Vendor Email</th>
                    <th>Consultant Email</th>
                    <th>Month</th>
                    <th>Year</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <span className="loader"></span>
                      </td>
                    </tr>
                  ) : currentRecords.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <div className="no-data-wrapper">
                          <div className="no-data-found">
                            <i className="ri-file-damage-fill"></i>
                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                            <p>No Invoices Found</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((inv, idx) => (
                      <tr key={inv.invoiceId}>
                        <td>{inv.invoiceId}</td>
                        <td>{inv.agency}</td>
                        <td
                          style={{
                            maxWidth: '150px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={inv.agencyEmail}
                        >
                          {inv.agencyEmail}
                        </td>
                        <td
                          style={{
                            maxWidth: '150px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={inv.consultant}
                        >
                          {inv.consultant}
                        </td>
                        <td>{inv.month}</td>
                        <td>{inv.year}</td>
                        <td>
                          <span className={`badge border ${inv.status === "Generated" || inv.status === "Paid" ? "border-success text-success" : inv.status === "Rejected" ? "border-danger text-danger" : "border-secondary text-secondary"} status`}>
                            {inv.status}
                          </span>
                        </td>
                        <td>
                          {renderActions(inv)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Pagination */}
        <div className="col-md-12">
          <div className="container-fluid">
            <div className="row border-top pt-3">
              <div className="col-auto">
                <p className="mt-1 mb-0">Records</p>
              </div>
              <div className="col-1.2 record-drop">
                <select className="form-control" value={recordsPerPage} onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                  <option value="25">25</option>
                </select>
              </div>
              <div className="col">
                <nav>
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                        <i className="ri-arrow-left-double-line"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(index + 1)}>
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => paginate(totalPages)}>
                        <i className="ri-arrow-right-double-fill"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
              <div className="col-auto">
                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Email Modal */}
        <Modal
          title="Send Invoice via Email"
          visible={isEmailModalVisible}
          onCancel={handleEmailModalCancel}
          footer={null}
        >
          <Form
            form={emailForm}
            layout="vertical"
            onFinish={handleEmailSend}
          >
            <Form.Item
              name="email"
              label="Recipient Email"
              rules={[
                { required: true, message: "Please enter recipient email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={sendingEmail}
                  style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                >
                  Send
                </Button>
                <Button onClick={handleEmailModalCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* View Modal */}
        <Modal
          title={`Invoice Details - ${selectedInvoice?.invoiceId}`}
          visible={isViewModalVisible}
          onCancel={handleViewModalCancel}
          width={800}
          footer={[
            <Button key="close" onClick={handleViewModalCancel}>
              Close
            </Button>,
            <Button
              key="view"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleGenerateInvoice(selectedInvoice)}
              style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
            >
              View PDF
            </Button>,
          ]}
        >
          {selectedInvoice && (
            <div style={{ height: "600px", overflow: "auto" }}>
              {selectedInvoice.invoiceFile ? (
                <iframe
                  src={selectedInvoice.invoiceFile}
                  width="100%"
                  height="100%"
                  title={`Invoice ${selectedInvoice.invoiceId}`}
                  style={{ border: "none" }}
                />
              ) : (
                <p>No invoice file available for this invoice.</p>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}

export default VendorInvoicesDashboardPage;